import React, { useState, useEffect, useRef } from 'react';
import { formatShortDate } from '../features/dashboard/calendarDate'; import { drillHref, DrillLink } from '../features/drilldown/drillLinks';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ErrorBoundary from '../components/ErrorBoundary';
import { allowedPagesFor, canOpen, isRoleRefusal } from '../utils/rolePages';
import AuthBrand, { useAppBrand } from '../components/AuthBrand';
import { accountsAPI, accountsReceivableAPI, subscriptionsAPI, recurringBillsAPI, dashboardAPI, assetExpensesAPI, reconciliationAPI, tasksAPI, businessesAPI } from '../services/api';
import { formatApiError } from '../utils/errorMessage';
import { startPlanCheckout } from '../services/billingApi';
import { findAccountPlan, planAmount, planCycleLabel } from '../features/plans/accountPlans';
import { readPlanChoice, clearPlanChoice, locationKindForPlan } from '../features/plans/planChoice';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ExclamationTriangleIcon, CheckCircleIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { localDateKey, parseCalendarDay } from '../utils/localDate';
import { LockedCard, isLockedError } from '../features/dashboard/lockedCard';
import { SetupChecklistCard } from '../features/businessSetup/SetupChecklist';
import { signedAmountText, directionClass } from '../utils/transactionDirection';
function EmptyStart() {
  const appConfig = useAppBrand();
  const brandColor = appConfig?.primary_color || '#3B82F6';
  const [planChoice] = useState(() => readPlanChoice());
  const chosenPlan = planChoice ? findAccountPlan(planChoice.plan) : null;
  const [name, setName] = useState('');
  const [kind, setKind] = useState(() => (planChoice ? locationKindForPlan(planChoice.plan) : 'Personal'));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [createdId, setCreatedId] = useState(null);

  const openCheckout = async (businessId) => {
    if (!planChoice) {
      window.location.assign('/billing');
      return;
    }
    const result = await startPlanCheckout({ businessId, plan: planChoice.plan, billingCycle: planChoice.cycle });
    if (!result.redirected) {
      clearPlanChoice();
      window.location.assign('/dashboard');
    }
  };

  const addLocation = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    let businessId = createdId;
    try {
      if (!businessId) {
        const created = await businessesAPI.create({ name: name.trim(), legal_structure: kind });
        businessId = created.data.id;
        setCreatedId(businessId);
        localStorage.setItem('selectedBusinessId', String(businessId)); // same key as utils/businessSelection
      }
    } catch (err) {
      setError(formatApiError(err, 'Could not add the location'));
      setSaving(false);
      return;
    }
    try {
      await openCheckout(businessId);
    } catch (err) {
      setError(formatApiError(err, 'The location was added, but checkout did not open. Try again, or choose a plan on Billing.'));
      setSaving(false);
    }
  };

  return (
    <div data-empty-start="true" className="min-h-[70vh] bg-gray-100 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        <AuthBrand appConfig={appConfig} />
        <h2 className="mt-2 text-center text-2xl font-bold text-gray-900">Add a person or business</h2>
        {chosenPlan && (
          <p className="mt-2 text-center text-sm text-gray-600">
            {chosenPlan.name} · ${planAmount(chosenPlan, planChoice.cycle)} a {planCycleLabel(planChoice.cycle)}.
            {' '}
            {chosenPlan.trialDays
              ? `After this step you add a card. The first ${chosenPlan.trialDays} days are free; cancel before then and you are not charged.`
              : 'After this step you add a card to start the plan.'}
          </p>
        )}
        <form onSubmit={addLocation} className="mt-6 bg-white p-8 shadow rounded-lg space-y-6">
          {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
          <div>
            <label htmlFor="location-name" className="block text-sm font-medium text-gray-700">Name</label>
            <input
              id="location-name"
              required
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="mt-1 appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none sm:text-sm"
            />
          </div>
          <div>
            <label htmlFor="location-kind" className="block text-sm font-medium text-gray-700">Type</label>
            <select
              id="location-kind"
              value={kind}
              onChange={(event) => setKind(event.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm sm:text-sm"
            >
              <option value="Personal">Personal</option>
              <option value="Sole Proprietorship">Sole Proprietorship</option>
              <option value="LLC">LLC</option>
              <option value="Corporation">Corporation</option>
              <option value="S-Corp">S-Corp</option>
              <option value="Partnership">Partnership</option>
              <option value="Non-Profit">Non-Profit</option>
            </select>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white disabled:bg-gray-400"
            style={{ backgroundColor: saving ? undefined : brandColor }}
          >
            {saving ? 'Adding...' : (createdId ? 'Continue to checkout' : 'Add location')}
          </button>
        </form>
      </div>
    </div>
  );
}

// The stored value is the location id (or "all") and nothing else.
const FIRST_STEPS_KEY_PREFIX = 'elonest.firstSteps.dismissed.';

function firstStepsDismissed(scope) {
  try {
    return localStorage.getItem(`${FIRST_STEPS_KEY_PREFIX}${scope}`) === scope;
  } catch (error) {
    return false;
  }
}

function FirstStepsCard({ scope, onDismiss }) {
  const dismiss = () => {
    try {
      localStorage.setItem(`${FIRST_STEPS_KEY_PREFIX}${scope}`, scope);
    } catch (error) {
      // Hidden for this visit only if the browser refuses storage.
    }
    onDismiss(scope);
  };

  return (
    <section aria-labelledby="first-steps-title" className="bg-white border border-gray-200 rounded-lg shadow p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 id="first-steps-title" className="text-base font-semibold text-gray-900">Get started</h2>
          <p className="mt-1 text-sm text-gray-700">
            There are no accounts here yet. Add the bank or card account you use, then bring in its transactions.
          </p>
        </div>
        <button type="button" onClick={dismiss} className="text-sm text-gray-600 hover:text-gray-900">
          Hide
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-3">
        <Link to="/accounts" className="px-4 py-2 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700">
          Add your first account
        </Link>
        <Link to="/transactions" className="px-4 py-2 bg-white border border-gray-300 rounded text-sm font-medium text-gray-800 hover:bg-gray-50">
          Import or add transactions
        </Link>
      </div>
    </section>
  );
}

const Dashboard = ({ selectedBusiness }) => {
  const location = useLocation(); const navigate = useNavigate(); const openSlice = (s) => navigate(drillHref({ metric: 'expenses', start: appliedStartDate, end: appliedEndDate, business: selectedBusiness, category: s?.category_id ?? 'uncategorized' }));
  const isInitialMount = useRef(true);
  const prevLocationRef = useRef(location.pathname);

  // Get first and last day of current month as default date range
  const getDefaultDateRange = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      start: localDateKey(firstDay),
      end: localDateKey(lastDay)
    };
  };

  // Initialize dates: use localStorage if available, otherwise default to current month
  const getInitialDates = () => {
    try {
      const savedStart = localStorage.getItem('dashboard_startDate');
      const savedEnd = localStorage.getItem('dashboard_endDate');
      const savedAppliedStart = localStorage.getItem('dashboard_appliedStartDate');
      const savedAppliedEnd = localStorage.getItem('dashboard_appliedEndDate');
      // Check if saved dates are from current month
      const defaultRange = getDefaultDateRange();
      const now = new Date();
      const currentMonthStart = localDateKey(new Date(now.getFullYear(), now.getMonth(), 1));
      // A saved range from an earlier month has no applied dates on defaultRange.
      if (savedStart && savedStart < currentMonthStart) {
        localStorage.setItem('dashboard_startDate', defaultRange.start);
        localStorage.setItem('dashboard_endDate', defaultRange.end);
        localStorage.setItem('dashboard_appliedStartDate', defaultRange.start);
        localStorage.setItem('dashboard_appliedEndDate', defaultRange.end);
        return {
          start: defaultRange.start,
          end: defaultRange.end,
          appliedStart: defaultRange.start,
          appliedEnd: defaultRange.end
        };
      }
      if (savedStart && savedEnd && savedAppliedStart && savedAppliedEnd) {
        return {
          start: savedStart,
          end: savedEnd,
          appliedStart: savedAppliedStart,
          appliedEnd: savedAppliedEnd
        };
      }
    } catch (error) {
      console.error('Error loading saved dates:', error?.response?.status || error?.code || 'no response', error?.response?.data?.code || '');
    }
    // Default to current month
    const defaultRange = getDefaultDateRange();
    return {
      start: defaultRange.start,
      end: defaultRange.end,
      appliedStart: defaultRange.start,
      appliedEnd: defaultRange.end
    };
  };

  const initialDates = getInitialDates();
  const [startDate, setStartDate] = useState(initialDates.start);
  const [endDate, setEndDate] = useState(initialDates.end);
  const [appliedStartDate, setAppliedStartDate] = useState(initialDates.appliedStart);
  const [appliedEndDate, setAppliedEndDate] = useState(initialDates.appliedEnd);

  // Query parameters
  const queryParams = {
    ...(selectedBusiness ? { business_id: selectedBusiness } : {}),
    start_date: appliedStartDate,
    end_date: appliedEndDate
  };

  // React Query hooks for all dashboard data
  const { data: locationList, isLoading: locationsLoading, isError: locationsFailed, refetch: reloadLocations } = useQuery({
    queryKey: ['dashboard-locations'],
    queryFn: () => businessesAPI.getAll().then((res) => (Array.isArray(res.data) ? res.data : [])),
    staleTime: 60 * 1000
  });
  const showFigures = !locationsLoading && !locationsFailed && Array.isArray(locationList) && locationList.length > 0;
  const sampleBusinessId = selectedBusiness || (showFigures && locationList[0] && locationList[0].id) || null;
  // What this role may open at the selected location (server's allowed_pages).
  // Sections outside it are not requested and not shown.
  const allowedPages = allowedPagesFor(locationList, selectedBusiness);
  const can = (page) => canOpen(allowedPages, page);

  const { data: sampleBooks, isPending: samplePending } = useQuery({
    queryKey: ['location-sample', sampleBusinessId],
    queryFn: () => businessesAPI.sampleStatus(sampleBusinessId).then((res) => res.data),
    enabled: Boolean(showFigures && sampleBusinessId),
    staleTime: 60 * 1000
  });
  const firstStepsScope = selectedBusiness ? String(selectedBusiness) : 'all';
  const { data: firstStepsAccounts, isPending: accountsPending, isError: accountsFailed } = useQuery({
    queryKey: ['dashboard-first-steps-accounts', firstStepsScope],
    queryFn: () => accountsAPI.getAll(selectedBusiness ? { business_id: selectedBusiness } : {})
      .then((res) => (Array.isArray(res.data) ? res.data : null)),
    enabled: Boolean(showFigures) && can('/accounts'),
    staleTime: 60 * 1000
  });
  const [dismissedScopes, setDismissedScopes] = useState(() => new Set());
  const showFirstSteps = showFigures
    && !accountsPending && !accountsFailed && Array.isArray(firstStepsAccounts)
    && !firstStepsAccounts.some((account) => account.is_active !== false)
    && !samplePending && !(sampleBooks?.seeded && !sampleBooks?.cleared)
    && !dismissedScopes.has(firstStepsScope) && !firstStepsDismissed(firstStepsScope);
  const hideFirstSteps = (scope) => setDismissedScopes((current) => new Set(current).add(scope));
  const [clearingSample, setClearingSample] = useState(false);
  const clearSampleBooks = async () => {
    if (!sampleBusinessId) return;
    setClearingSample(true);
    try {
      await businessesAPI.clearSample(sampleBusinessId);
      window.location.assign('/dashboard');
    } catch (err) {
      setClearingSample(false);
    }
  };

  const { data: overview, error: overviewError, isLoading: overviewLoading } = useQuery({
    queryKey: ['dashboard-overview', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: () => dashboardAPI.getOverview(queryParams).then(res => res.data),
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && Boolean(appliedStartDate && appliedEndDate)
  });

  const { data: dailySpendingData, error: dailySpendingError, isLoading: dailySpendingLoading } = useQuery({
    queryKey: ['dashboard-daily', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: async () => {
      const start = parseCalendarDay(appliedStartDate);
      const end = parseCalendarDay(appliedEndDate);
      const daysDiff = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
      const maxDays = Math.min(daysDiff, 30);
      const res = await dashboardAPI.getDailySpending({ ...queryParams, limit: maxDays });
      const formattedData = (res.data || []).map(item => ({
        ...item,
        total_expenses: Math.abs(parseFloat(item.total_expenses || 0)),
        total_income: Math.abs(parseFloat(item.total_income || 0))
      })).sort((a, b) => parseCalendarDay(a.transaction_date) - parseCalendarDay(b.transaction_date));
      return formattedData;
    },
    staleTime: 5 * 60 * 1000,
    enabled: showFigures
  });
  const dailySpending = dailySpendingData || [];

  const { data: spendingByCategoryData, error: spendingByCategoryError, isLoading: categoryLoading } = useQuery({
    queryKey: ['dashboard-categories', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: async () => {
      const res = await dashboardAPI.getSpendingByCategory(queryParams);
      return (res.data || []).map(item => ({
        ...item,
        total_amount: parseFloat(item.total_amount || 0),
        transaction_count: parseInt(item.transaction_count || 0),
        avg_amount: parseFloat(item.avg_amount || 0),
        category_name: item.category_name || 'Unknown',
        color: item.color || '#3b82f6'
      })).filter(item => item.total_amount > 0);
    },
    staleTime: 5 * 60 * 1000,
    enabled: showFigures
  });
  const spendingByCategory = spendingByCategoryData || [];

  const { data: recentTransactionsData, error: recentTransactionsError } = useQuery({
    queryKey: ['dashboard-recent-transactions', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: () => dashboardAPI.getRecentTransactions({ ...queryParams, limit: 5 }).then(res => res.data),
    staleTime: 2 * 60 * 1000,
    enabled: showFigures
  });
  const recentTransactions = recentTransactionsData || [];

  const { data: monthlyTrendsData, error: monthlyTrendsError } = useQuery({
    queryKey: ['dashboard-monthly-trends', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: () => dashboardAPI.getMonthlyTrends(queryParams).then(res => res.data),
    staleTime: 5 * 60 * 1000,
    enabled: showFigures
  });
  const monthlyTrends = monthlyTrendsData || [];

  const { data: assetExpenseSummaryData, error: assetExpenseError } = useQuery({
    queryKey: ['dashboard-asset-expenses', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: () => assetExpensesAPI.getSummaryByAsset(queryParams).then(res => res.data),
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && can('/asset-expenses')
  });
  const assetExpenseSummary = assetExpenseSummaryData || [];

  const { data: reconciliationsData, error: reconciliationsError } = useQuery({
    queryKey: ['dashboard-reconciliations', selectedBusiness],
    queryFn: () => reconciliationAPI.getAll({ ...queryParams, status: 'in_progress', limit: 3 }).then(res => res.data),
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && can('/reconciliation')
  });
  const reconciliations = reconciliationsData || [];

  const { data: taskStats, error: taskStatsError } = useQuery({
    queryKey: ['dashboard-task-stats', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: () => tasksAPI.getStats(queryParams).then(res => res.data),
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && can('/tasks')
  });

  const { data: overdueTasksData, error: overdueTasksError } = useQuery({
    queryKey: ['dashboard-overdue-tasks', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: () => tasksAPI.getOverdue({ ...queryParams, limit: 5 }).then(res => res.data),
    staleTime: 2 * 60 * 1000,
    enabled: showFigures && can('/tasks')
  });
  const overdueTasks = overdueTasksData || [];

  const { data: netWorth, error: netWorthError } = useQuery({
    queryKey: ['dashboard-net-worth', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: () => dashboardAPI.getNetWorth(queryParams).then(res => res.data),
    staleTime: 5 * 60 * 1000,
    enabled: showFigures
  });

  const { data: arProjected, error: arProjectedError } = useQuery({
    queryKey: ['dashboard-ar-projected', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: () => {
      if (!appliedStartDate || !appliedEndDate || appliedStartDate > appliedEndDate) {
        return Promise.resolve({ projected_amount: 0, invoice_count: 0 });
      }
      return accountsReceivableAPI.getProjected({
        ...(selectedBusiness ? { business_id: selectedBusiness } : {}),
        start_date: appliedStartDate,
        end_date: appliedEndDate
      }).then(res => res.data);
    },
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && !!appliedStartDate && !!appliedEndDate && appliedStartDate <= appliedEndDate && can('/accounts-receivable')
  });

  const { data: arSummary, error: arSummaryError } = useQuery({
    queryKey: ['dashboard-ar-summary', selectedBusiness],
    queryFn: () => accountsReceivableAPI.getSummary(selectedBusiness ? { business_id: selectedBusiness } : {}).then(res => res.data),
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && can('/accounts-receivable')
  });

  const { data: subsUpcomingData, error: subsUpcomingError } = useQuery({
    queryKey: ['dashboard-subs-upcoming', selectedBusiness],
    queryFn: async () => {
      const res = await subscriptionsAPI.getUpcoming(selectedBusiness ? { business_id: selectedBusiness } : {});
      const list = res.data || [];
      return { count: list.length, total: list[0]?.list_total || 0 };
    },
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && can('/subscriptions')
  });
  const subsUpcoming = subsUpcomingData || { count: 0, total: 0 };

  const { data: billsUpcomingData, error: billsUpcomingError } = useQuery({
    queryKey: ['dashboard-bills-upcoming', selectedBusiness],
    queryFn: async () => {
      const res = await recurringBillsAPI.getUpcoming(selectedBusiness ? { business_id: selectedBusiness } : {});
      const list = res.data || [];
      return { count: list.length, total: list[0]?.list_total || 0 };
    },
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && can('/recurring-bills')
  });
  const billsUpcoming = billsUpcomingData || { count: 0, total: 0 };

  const { data: reconStatusData, error: reconStatusError } = useQuery({
    queryKey: ['dashboard-recon-status', selectedBusiness],
    queryFn: async () => {
      const scope = selectedBusiness ? { business_id: selectedBusiness } : {};
      const inProg = await reconciliationAPI.getAll({ ...scope, is_complete: 'false' });
      // The moment it was completed (not the statement/period end date,
      // which can be in the future), labeled in the account's time zone.
      let lastCompleted = null;
      try {
        const last = await reconciliationAPI.lastCompleted(scope);
        lastCompleted = last?.data?.completed_at ? (last.data.label || last.data.date_label || null) : null;
      } catch {}
      return { inProgress: Array.isArray(inProg.data) ? inProg.data.length : 0, lastCompleted };
    },
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && can('/reconciliation')
  });
  const reconStatus = reconStatusData || { inProgress: 0, lastCompleted: null };

  // Rollup query (separate because it has different dependencies)
  const { data: rollup, isLoading: rollupLoading, error: rollupError } = useQuery({
    queryKey: ['dashboard-rollup', selectedBusiness, appliedStartDate, appliedEndDate],
    queryFn: () => businessesAPI.getFinancialRollup(selectedBusiness, { start_date: appliedStartDate, end_date: appliedEndDate }).then(res => res.data),
    staleTime: 5 * 60 * 1000,
    enabled: showFigures && !!selectedBusiness
  });

  // Calculate overall loading state
  const loading = locationsLoading || overviewLoading || dailySpendingLoading || categoryLoading;

  // Reset dates to current month when navigating from another page
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevLocationRef.current = location.pathname;
      // On initial mount, ensure dates are saved to localStorage
      const defaultRange = getDefaultDateRange();
      if (!localStorage.getItem('dashboard_startDate')) {
        localStorage.setItem('dashboard_startDate', defaultRange.start);
        localStorage.setItem('dashboard_endDate', defaultRange.end);
        localStorage.setItem('dashboard_appliedStartDate', defaultRange.start);
        localStorage.setItem('dashboard_appliedEndDate', defaultRange.end);
      }
    } else if (prevLocationRef.current !== location.pathname) {
      // User navigated from another page - reset to current month
      prevLocationRef.current = location.pathname;
      const defaultRange = getDefaultDateRange();
      setStartDate(defaultRange.start);
      setEndDate(defaultRange.end);
      setAppliedStartDate(defaultRange.start);
      setAppliedEndDate(defaultRange.end);
      localStorage.setItem('dashboard_startDate', defaultRange.start);
      localStorage.setItem('dashboard_endDate', defaultRange.end);
      localStorage.setItem('dashboard_appliedStartDate', defaultRange.start);
      localStorage.setItem('dashboard_appliedEndDate', defaultRange.end);
    }
  }, [location.pathname]);

  // React Query automatically refetches when dependencies change

  const handleApplyDateRange = () => {
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    // Save applied dates to localStorage
    localStorage.setItem('dashboard_startDate', startDate);
    localStorage.setItem('dashboard_endDate', endDate);
    localStorage.setItem('dashboard_appliedStartDate', startDate);
    localStorage.setItem('dashboard_appliedEndDate', endDate);
  };

  const handleResetDates = () => {
    const defaultRange = getDefaultDateRange();
    setStartDate(defaultRange.start);
    setEndDate(defaultRange.end);
    setAppliedStartDate(defaultRange.start);
    setAppliedEndDate(defaultRange.end);
    // Save to localStorage
    localStorage.setItem('dashboard_startDate', defaultRange.start);
    localStorage.setItem('dashboard_endDate', defaultRange.end);
    localStorage.setItem('dashboard_appliedStartDate', defaultRange.start);
    localStorage.setItem('dashboard_appliedEndDate', defaultRange.end);
  };

  const handleQuickFilter = (days) => {
    const today = new Date();
    const futureDate = new Date(today);
    futureDate.setDate(today.getDate() + days);
    const start = localDateKey(today);
    const end = localDateKey(futureDate);
    
    setStartDate(start);
    setEndDate(end);
    setAppliedStartDate(start);
    setAppliedEndDate(end);
    // Save to localStorage
    localStorage.setItem('dashboard_startDate', start);
    localStorage.setItem('dashboard_endDate', end);
    localStorage.setItem('dashboard_appliedStartDate', start);
    localStorage.setItem('dashboard_appliedEndDate', end);
  };

  // Format errors from React Query
  // A role refusal is not an error card: that section is simply not shown.
  const formatQueryError = (error) => (error && !isRoleRefusal(error) ? formatApiError(error, 'This section could not be loaded') : null);

  // Convert React Query errors to formatted strings for display
  const overviewErrorFormatted = formatQueryError(overviewError);
  const dailySpendingErrorFormatted = formatQueryError(dailySpendingError);
  const spendingByCategoryErrorFormatted = formatQueryError(spendingByCategoryError);
  const recentTransactionsErrorFormatted = formatQueryError(recentTransactionsError);
  const monthlyTrendsErrorFormatted = formatQueryError(monthlyTrendsError);
  const assetExpenseErrorFormatted = formatQueryError(assetExpenseError);
  const reconciliationsErrorFormatted = formatQueryError(reconciliationsError);
  const taskStatsErrorFormatted = formatQueryError(taskStatsError);
  const overdueTasksErrorFormatted = formatQueryError(overdueTasksError);
  const netWorthErrorFormatted = formatQueryError(netWorthError);
  const arProjectedLocked = isLockedError(arProjectedError);
  const arSummaryLocked = isLockedError(arSummaryError);
  const arProjectedErrorFormatted = arProjectedLocked ? null : formatQueryError(arProjectedError);
  const arSummaryErrorFormatted = arSummaryLocked ? null : formatQueryError(arSummaryError);
  const subsUpcomingErrorFormatted = formatQueryError(subsUpcomingError);
  const billsUpcomingErrorFormatted = formatQueryError(billsUpcomingError);
  const reconStatusErrorFormatted = formatQueryError(reconStatusError);
  const rollupErrorFormatted = formatQueryError(rollupError);
  const dashboardLoadFailed = [
    overviewError, dailySpendingError, spendingByCategoryError, recentTransactionsError, monthlyTrendsError,
    assetExpenseError, reconciliationsError, taskStatsError, overdueTasksError, netWorthError,
    arProjectedLocked ? null : arProjectedError, arSummaryLocked ? null : arSummaryError, subsUpcomingError, billsUpcomingError, reconStatusError, rollupError
  ].some(Boolean);

  // Helper functions
  const safeNumber = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
  const formatCurrency = (amount) => {
    try { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(safeNumber(amount)); } catch { return '$0.00'; }
  };

  const formatDate = formatShortDate;

  const formatPeriodDate = (value) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) return value || '';
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-2">
          <div className="text-xl text-gray-700 font-semibold">Loading your eloNest financial overview…</div>
          <div className="text-sm text-gray-500">
            Tip: Use the business selector in the sidebar to switch between companies or personal finances.
          </div>
        </div>
      </div>
    );
  }

  if (locationsFailed) {
    return (
      <div className="max-w-xl space-y-3">
        <p className="text-gray-800">Locations did not load.</p>
        <button type="button" onClick={() => reloadLocations()} className="px-4 py-2 bg-blue-600 text-white rounded">Try again</button>
      </div>
    );
  }

  if (!showFigures) {
    return <EmptyStart />;
  }

  return (
    <ErrorBoundary section="Dashboard Page">
      <div className="space-y-6">
      {dashboardLoadFailed && (
        <div className="p-3 bg-red-50 text-red-700 border border-red-200 rounded" role="alert">
          Some dashboard sections could not be loaded. Refresh the page to try again.
        </div>
      )}
      {sampleBooks?.seeded && !sampleBooks?.cleared && (
        <div className="bg-white border border-gray-200 rounded-lg shadow p-4 flex items-center justify-between gap-4">
          <p className="text-sm text-gray-800">Sample figures are on this page.</p>
          <button
            type="button"
            onClick={clearSampleBooks}
            disabled={clearingSample}
            className="px-4 py-2 bg-white border border-gray-300 rounded text-sm text-gray-800 disabled:text-gray-400"
          >
            {clearingSample ? 'Clearing...' : 'Clear sample data'}
          </button>
        </div>
      )}
      {showFirstSteps && <FirstStepsCard scope={firstStepsScope} onDismiss={hideFirstSteps} />}
      {showFigures && /^\d+$/.test(String(selectedBusiness || '')) && <SetupChecklistCard businessId={Number(selectedBusiness)} />}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
        <div className="font-semibold mb-1">Welcome to eloNest - Complete Financial Control with Exceptional Service</div>
        <p>
          This eloNest dashboard provides comprehensive financial oversight, tracking cash flow, upcoming bills, subscriptions, and tasks.
          Select a business or personal account on the left, then use Transactions, Budgets, and Recurring Bills to maintain complete financial control.
        </p>
      </div>

      {/* Date Range Filter */}
      <ErrorBoundary>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex flex-col gap-4">
            {/* Date Inputs Row */}
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-sm font-medium text-gray-700">Data from</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={handleApplyDateRange}
                className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition text-sm font-medium"
              >
                Apply
              </button>
              <button
                onClick={handleResetDates}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition text-sm font-medium"
              >
                Reset
              </button>
            </div>

            {/* Quick Filter Buttons Row */}
            <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-gray-200">
              <span className="text-xs font-medium text-gray-600 uppercase">Quick Projections:</span>
              <ErrorBoundary>
                <button
                  onClick={() => handleQuickFilter(30)}
                  className="bg-green-50 text-green-700 px-4 py-1.5 rounded-md hover:bg-green-100 transition text-sm font-medium border border-green-200"
                >
                  Next 30 Days
                </button>
              </ErrorBoundary>
              <ErrorBoundary>
                <button
                  onClick={() => handleQuickFilter(60)}
                  className="bg-blue-50 text-blue-700 px-4 py-1.5 rounded-md hover:bg-blue-100 transition text-sm font-medium border border-blue-200"
                >
                  Next 60 Days
                </button>
              </ErrorBoundary>
              <ErrorBoundary>
                <button
                  onClick={() => handleQuickFilter(90)}
                  className="bg-purple-50 text-purple-700 px-4 py-1.5 rounded-md hover:bg-purple-100 transition text-sm font-medium border border-purple-200"
                >
                  Next 90 Days
                </button>
              </ErrorBoundary>
              <span className="text-xs text-gray-500 ml-auto">
                Showing data from {formatPeriodDate(appliedStartDate)} to {formatPeriodDate(appliedEndDate)}
              </span>
            </div>
          </div>
        </div>
      </ErrorBoundary>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <ErrorBoundary>
          {overviewErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load buying power</p>
              <p className="text-xs text-red-500 mt-1">{overviewErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600" title={overview?.buying_power_formula || 'Buying power = cash in asset accounts + available credit'}>Buying Power <span aria-label="How buying power is calculated" className="cursor-help text-gray-400">(i)</span></p>
                  <p className="text-2xl font-bold text-gray-900"><DrillLink to={drillHref({ kind: 'formula', formula: 'buying-power', business: selectedBusiness })}>{formatCurrency(overview?.buying_power || 0)}</DrillLink></p>
                  <p className="text-xs text-gray-500 mt-1" data-testid="buying-power-formula">Cash <DrillLink to={drillHref({ kind: 'accounts', side: 'liquid', business: selectedBusiness })}>{formatCurrency(overview?.buying_power_parts?.cash_assets || 0)}</DrillLink> + available credit <DrillLink to={drillHref({ kind: 'accounts', side: 'credit', business: selectedBusiness })}>{formatCurrency(overview?.buying_power_parts?.available_credit || 0)}</DrillLink></p>
                </div>
                <div className="text-4xl">💰</div>
              </div>
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary>
          {overviewErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load income</p>
              <p className="text-xs text-red-500 mt-1">{overviewErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Monthly Income</p>
                  <p className="text-2xl font-bold text-green-600"><DrillLink to={drillHref({ metric: 'income', start: appliedStartDate, end: appliedEndDate, business: selectedBusiness })}>{formatCurrency(overview?.monthly_income || 0)}</DrillLink></p>
                  <div className="mt-2 text-xs text-gray-500">
                    <div>Transactions: {formatCurrency(overview?.actual_monthly_income || 0)}</div>
                    <div>AR Projected: {formatCurrency(overview?.ar_monthly_income || 0)}</div>
                  </div>
                </div>
                <div className="text-4xl">📈</div>
              </div>
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary>
          {overviewErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load expenses</p>
              <p className="text-xs text-red-500 mt-1">{overviewErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Monthly Expenses</p>
                  <p className="text-2xl font-bold text-red-600"><DrillLink to={drillHref({ metric: 'expenses', start: appliedStartDate, end: appliedEndDate, business: selectedBusiness })}>{formatCurrency(overview?.monthly_expenses || 0)}</DrillLink></p>
                  <div className="mt-2 text-xs text-gray-500">
                    <div>Transactions: {formatCurrency(overview?.actual_monthly_expenses || 0)}</div>
                    <div>Subscriptions (per month, all active): {formatCurrency(overview?.subscription_monthly_expense || 0)}</div>
                    <div>Recurring bills (per month, all active): {formatCurrency(overview?.bills_monthly_expense || 0)}</div>
                    {(overview?.monthly_payroll || 0) > 0 && <div>Estimated payroll: {formatCurrency(overview?.monthly_payroll || 0)}</div>}
                  </div>
                </div>
                <div className="text-4xl">📉</div>
              </div>
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary>
          {overviewErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load net income</p>
              <p className="text-xs text-red-500 mt-1">{overviewErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Monthly Net</p>
                  <p className={`text-2xl font-bold ${overview?.monthly_net >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <DrillLink to={drillHref({ metric: 'net', start: appliedStartDate, end: appliedEndDate, business: selectedBusiness })}>{formatCurrency(overview?.monthly_net || 0)}</DrillLink>
                  </p>
                </div>
                <div className="text-4xl">{overview?.monthly_net >= 0 ? '✅' : '⚠️'}</div>
              </div>
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary hidden={!can('/accounts-receivable')}>
          {arProjectedLocked ? <LockedCard title="AR Projected Income" error={arProjectedError} /> : arProjectedErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load AR projected</p>
              <p className="text-xs text-red-500 mt-1">{arProjectedErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">AR Projected Income</p>
                  <p className="text-2xl font-bold text-blue-700"><DrillLink to={drillHref({ kind: 'ar-projected', start: appliedStartDate, end: appliedEndDate, business: selectedBusiness })}>{formatCurrency(arProjected?.projected_amount || 0)}</DrillLink></p>
                </div>
              </div>
            </div>
          )}
        </ErrorBoundary>
      </div>

      {/* AR Summary & Ops Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <ErrorBoundary hidden={!can('/accounts-receivable')}>
          {arSummaryLocked ? <LockedCard title="AR Outstanding" error={arSummaryError} /> : arSummaryErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load AR outstanding</p>
              <p className="text-xs text-red-500 mt-1">{arSummaryErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">AR Outstanding</div>
              <div className="text-2xl font-bold text-blue-700"><DrillLink to={drillHref({ kind: 'ar', business: selectedBusiness })}>{formatCurrency(arSummary?.total_outstanding || 0)}</DrillLink></div>
              <div className="text-xs text-gray-500 mt-1">Overdue: {formatCurrency(arSummary?.overdue_amount || 0)}</div>
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary hidden={!can('/accounts-receivable')}>
          {arSummaryLocked ? <LockedCard title="AR Aging" error={arSummaryError} /> : arSummaryErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load AR aging</p>
              <p className="text-xs text-red-500 mt-1">{arSummaryErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">AR Aging (0-30 • 31-60 • 61-90 • 90+)</div>
              <div className="mt-2 flex items-end gap-2 h-16">
                {(() => {
                  const a = [
                    arSummary?.current_0_30 || 0,
                    arSummary?.aging_31_60 || 0,
                    arSummary?.aging_61_90 || 0,
                    arSummary?.aging_over_90 || 0
                  ];
                  const max = Math.max(...a, 1);
                  const colors = ['bg-green-500','bg-yellow-500','bg-orange-500','bg-red-500'];
                  return a.map((v,i)=>(
                    <div key={i} className={`${colors[i]} w-1/4 rounded`} style={{height: `${(v/max)*100}%`}} title={formatCurrency(v)}></div>
                  ));
                })()}
              </div>
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary hidden={!can('/subscriptions')}>
          {subsUpcomingErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load subscriptions</p>
              <p className="text-xs text-red-500 mt-1">{subsUpcomingErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Subscriptions (Next 30d)</div>
              <div className="text-2xl font-bold text-gray-900"><DrillLink to="/subscriptions">{subsUpcoming.count}</DrillLink></div>
              <div className="text-xs text-gray-500 mt-1">Due in next 30 days: {formatCurrency(subsUpcoming.total)} · {overview?.subscriptions?.active_count ?? 0} active · {formatCurrency(overview?.subscriptions?.monthly_estimate || 0)}/month</div>
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary hidden={!can('/recurring-bills')}>
          {billsUpcomingErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load recurring bills</p>
              <p className="text-xs text-red-500 mt-1">{billsUpcomingErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Recurring Bills (Next 30d)</div>
              <div className="text-2xl font-bold text-gray-900"><DrillLink to="/recurring-bills">{billsUpcoming.count}</DrillLink></div>
              <div className="text-xs text-gray-500 mt-1">Due in next 30 days: {formatCurrency(billsUpcoming.total)} · {overview?.recurring_bills?.active_count ?? 0} active · {formatCurrency(overview?.recurring_bills?.monthly_estimate || 0)}/month</div>
            </div>
          )}
        </ErrorBoundary>
      </div>

      {/* Reconciliation Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ErrorBoundary hidden={!can('/reconciliation')}>
          {reconStatusErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load reconciliations in progress</p>
              <p className="text-xs text-red-500 mt-1">{reconStatusErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Reconciliations In Progress</div>
              <div className="text-2xl font-bold text-gray-900">{reconStatus.inProgress}</div>
            </div>
          )}
        </ErrorBoundary>
        
        <ErrorBoundary hidden={!can('/reconciliation')}>
          {reconStatusErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load last reconciliation</p>
              <p className="text-xs text-red-500 mt-1">{reconStatusErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Last Reconciliation Completed</div>
              <div className="text-2xl font-bold text-gray-900">{reconStatus.lastCompleted ? reconStatus.lastCompleted : '—'}</div>
            </div>
          )}
        </ErrorBoundary>
      </div>

      {/* Assets vs Liabilities */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <ErrorBoundary>
          {overviewErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load assets</p>
              <p className="text-xs text-red-500 mt-1">{overviewErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Assets (Accounts)</div>
              <div className="text-2xl font-bold text-gray-900"><DrillLink to={drillHref({ kind: 'accounts', side: 'liquid', business: selectedBusiness })}>{formatCurrency(overview?.assets_balance || 0)}</DrillLink></div>{overview?.unverified_count > 0 && <div className="text-xs text-amber-700" data-testid="unverified-dashboard">Unverified balance: {formatCurrency(overview.unverified_assets)} assets, {formatCurrency(overview.unverified_liabilities)} owed ({overview.unverified_count} accounts)</div>}{overview?.negative_asset_accounts > 0 && <div className="text-xs text-red-700">{overview.negative_asset_accounts} deposit account(s) below zero: review</div>}
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary>
          {overviewErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load liabilities</p>
              <p className="text-xs text-red-500 mt-1">{overviewErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Liabilities (Accounts)</div>
              <div className="text-2xl font-bold text-red-600"><DrillLink to={drillHref({ kind: 'accounts', side: 'liabilities', business: selectedBusiness })}>{formatCurrency(overview?.liabilities_balance || 0)}</DrillLink></div>
            </div>
          )}
        </ErrorBoundary>

        <ErrorBoundary>
          {overviewErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <p className="text-sm text-red-600">Failed to load net accounts</p>
              <p className="text-xs text-red-500 mt-1">{overviewErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="text-sm text-gray-600">Net Accounts</div>
              <div className={`text-2xl font-bold ${(overview?.net_accounts || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}><DrillLink to={drillHref({ kind: 'formula', formula: 'net-accounts', business: selectedBusiness })}>{formatCurrency(overview?.net_accounts || 0)}</DrillLink></div>
              {Number(overview?.accounts_to_check) > 0 && <div className="text-xs text-amber-700 mt-1">{overview.accounts_to_check} account(s) to check: overdrawn, or a debt account in credit</div>}
            </div>
          )}
        </ErrorBoundary>
      </div>

      {/* Net Worth */}
      {(netWorth || netWorthError) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ErrorBoundary>
            {netWorthErrorFormatted ? (
              <div className="bg-red-50 rounded-lg shadow p-6">
                <p className="text-sm text-red-600">Failed to load total assets</p>
                <p className="text-xs text-red-500 mt-1">{netWorthErrorFormatted}</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600">Assets</div>
                <div className="text-2xl font-bold text-gray-900"><DrillLink to={drillHref({ kind: 'total-assets', business: selectedBusiness })}>{formatCurrency(netWorth?.total_assets || 0)}</DrillLink></div>
              </div>
            )}
          </ErrorBoundary>

          <ErrorBoundary>
            {netWorthErrorFormatted ? (
              <div className="bg-red-50 rounded-lg shadow p-6">
                <p className="text-sm text-red-600">Failed to load total liabilities</p>
                <p className="text-xs text-red-500 mt-1">{netWorthErrorFormatted}</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600">Total Liabilities</div>
                <div className="text-2xl font-bold text-red-600"><DrillLink to={drillHref({ kind: 'accounts', side: 'liabilities', networth: true, business: selectedBusiness })}>{formatCurrency(netWorth?.total_liabilities || 0)}</DrillLink></div>
              </div>
            )}
          </ErrorBoundary>

          <ErrorBoundary>
            {netWorthErrorFormatted ? (
              <div className="bg-red-50 rounded-lg shadow p-6">
                <p className="text-sm text-red-600">Failed to load net worth</p>
                <p className="text-xs text-red-500 mt-1">{netWorthErrorFormatted}</p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="text-sm text-gray-600">Net worth</div>
                <div className={`text-2xl font-bold ${(netWorth?.net_worth || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}><DrillLink to={drillHref({ kind: 'formula', formula: 'net-worth', business: selectedBusiness })}>{formatCurrency(netWorth?.net_worth || 0)}</DrillLink></div>
              </div>
            )}
          </ErrorBoundary>
        </div>
      )}

      {/* Group Roll-Up (Parent + Subsidiaries) */}
      {selectedBusiness && (
        <ErrorBoundary>
          {rollupErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Combined</h3>
                <button 
                  onClick={() => window.location.reload()} 
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Retry
                </button>
              </div>
              <p className="text-sm text-red-600">Failed to load rollup data</p>
              <p className="text-xs text-red-500 mt-1">{rollupErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Combined</h3>
                {rollupLoading && <span className="text-sm text-gray-500">Loading...</span>}
              </div>

              {rollupLoading && !rollup && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-500">Loading financial rollup...</div>
                </div>
              )}

              {!rollupLoading && !rollup && (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-500">No rollup data available</div>
                </div>
              )}

              {rollup && (
            <>
              {/* Balance Sheet Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6 pb-6 border-b">
                {/* Assets */}
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="text-sm font-medium text-green-800 mb-2">Assets</div>
                  <div className="text-2xl font-bold text-green-900"><DrillLink to={drillHref({ kind: 'total-assets', rollup: true, business: selectedBusiness })}>{formatCurrency(rollup.total_assets || 0)}</DrillLink></div>
                  <div className="mt-3 space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Liquid Assets:</span>
                      <span className="font-semibold"><DrillLink to={drillHref({ kind: 'accounts', side: 'liquid', rollup: true, business: selectedBusiness })}>{formatCurrency(rollup.liquid_assets || 0)}</DrillLink></span>
                    </div>
                    <div className="pl-3 space-y-1 text-gray-500">
                      <div className="flex justify-between">
                        <span>• Checking:</span>
                        <span>{formatCurrency(rollup.checking_balance || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>• Savings:</span>
                        <span>{formatCurrency(rollup.savings_balance || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>• Investments:</span>
                        <span>{formatCurrency(rollup.investment_balance || 0)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-green-200">
                      <span className="text-gray-600">Fixed Assets:</span>
                      <span className="font-semibold">{formatCurrency(rollup.total_asset_value || 0)}</span>
                    </div>
                    <div className="pl-3 space-y-1 text-gray-500">
                      <div className="flex justify-between">
                        <span>• Vehicles:</span>
                        <span>{formatCurrency(rollup.vehicles_value || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>• Properties:</span>
                        <span>{formatCurrency(rollup.properties_value || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>• Other Assets:</span>
                        <span>{formatCurrency(rollup.other_assets_value || 0)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between pt-1 border-t border-green-200">
                      <span className="text-gray-600">AR Outstanding:</span>
                      <span className="font-semibold"><DrillLink to={drillHref({ kind: 'ar', rollup: true, business: selectedBusiness })}>{formatCurrency(rollup.ar_outstanding || 0)}</DrillLink></span>
                    </div>
                  </div>
                </div>

                {/* Liabilities */}
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="text-sm font-medium text-red-800 mb-2">💳 TOTAL LIABILITIES</div>
                  <div className="text-2xl font-bold text-red-900"><DrillLink to={drillHref({ kind: 'accounts', side: 'liabilities', rollup: true, business: selectedBusiness })}>{formatCurrency(rollup.total_liabilities || 0)}</DrillLink></div>
                  <div className="mt-3 space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Credit Cards:</span>
                      <span className="font-semibold">{formatCurrency(rollup.credit_card_balance || 0)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Other Liabilities:</span>
                      <span className="font-semibold">{formatCurrency(rollup.other_liabilities_balance || 0)}</span>
                    </div>
                  </div>
                </div>

                {/* Equity */}
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="text-sm font-medium text-blue-800 mb-2">💰 EQUITY</div>
                  <div className={`text-2xl font-bold ${(rollup.equity || 0) >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                    <DrillLink to={drillHref({ kind: 'formula', formula: 'equity', rollup: true, business: selectedBusiness })}>{formatCurrency(rollup.equity || 0)}</DrillLink>
                  </div>
                  <div className="mt-3 text-xs text-gray-600">
                    <div className="text-gray-500 mt-1">
                      {formatCurrency(rollup.total_assets || 0)} - {formatCurrency(rollup.total_liabilities || 0)}
                    </div>
                  </div>
                </div>
              </div>

              {/* Income Statement */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Total Income (selected period)</div>
                  <div className="text-xl font-bold text-green-600"><DrillLink to={drillHref({ metric: 'income', start: appliedStartDate, end: appliedEndDate, business: selectedBusiness })}>{formatCurrency(rollup.total_income || 0)}</DrillLink></div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Total Expenses (selected period)</div>
                  <div className="text-xl font-bold text-red-600"><DrillLink to={drillHref({ metric: 'expenses', start: appliedStartDate, end: appliedEndDate, business: selectedBusiness })}>{formatCurrency(rollup.total_expenses || 0)}</DrillLink></div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">Net Income (selected period)</div>
                  <div className={`text-xl font-bold ${(rollup.net || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    <DrillLink to={drillHref({ metric: 'net', start: appliedStartDate, end: appliedEndDate, business: selectedBusiness })}>{formatCurrency(rollup.net || 0)}</DrillLink>
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600">AR Overdue</div>
                  <div className="text-xl font-bold text-orange-600">{rollup.ar_overdue_count || 0}</div>
                </div>
              </div>
            </>
          )}
            </div>
          )}
        </ErrorBoundary>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Spending Chart - Candlestick */}
        <ErrorBoundary section="Daily Spending Section">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Daily Spending</h3>
            {dailySpendingErrorFormatted ? (
              <ErrorBoundary section="Daily Spending Error Display" compact>
                <div className="bg-red-50 border border-red-200 rounded p-4">
                  <p className="text-sm text-red-600">Failed to load daily spending data</p>
                  <p className="text-xs text-red-500 mt-1">{dailySpendingErrorFormatted}</p>
                  <button
                    onClick={() => window.location.reload()}
                    className="mt-2 text-sm text-red-700 underline hover:text-red-800"
                  >
                    Try again
                  </button>
                </div>
              </ErrorBoundary>
            ) : dailySpending.length > 0 ? (
              <ErrorBoundary section="Daily Spending Chart" compact>
                <ResponsiveContainer width="100%" height={450}>
                  {(() => {
                    // Calculate number of days to display (up to 30)
                    const daysToShow = Math.min(dailySpending.length, 30);
                    const chartData = dailySpending.slice(0, daysToShow);
                    
                    // Calculate interval for X-axis ticks based on number of days
                    // For 30 days, show every tick. For fewer days, adjust interval
                    let tickInterval = 0; // 0 means show all ticks
                    if (daysToShow > 20) {
                      tickInterval = Math.floor(daysToShow / 15); // Show approximately 15 ticks
                    }
                    
                    return (
                      <BarChart 
                        data={chartData}
                        margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.3} />
                        <XAxis 
                          dataKey="transaction_date" 
                          tickFormatter={(value) => {
                            try {
                              const date = new Date(value);
                              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            } catch {
                              return '';
                            }
                          }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                          interval={tickInterval}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis 
                          tickFormatter={(value) => {
                            try {
                              if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
                              return formatCurrency(value);
                            } catch {
                              return '$0';
                            }
                          }}
                          tick={{ fontSize: 11 }}
                          width={80}
                          domain={[0, 'dataMax']}
                        />
                        <Tooltip 
                          formatter={(value, name) => {
                            return [formatCurrency(value), name];
                          }}
                          labelFormatter={(label) => {
                            try {
                              const date = new Date(label);
                              return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
                            } catch {
                              return label;
                            }
                          }}
                          contentStyle={{ backgroundColor: '#fff', border: '1px solid #ccc', borderRadius: '4px', padding: '8px' }}
                        />
                        <Legend />
                        {/* Income bar - Green */}
                        <Bar 
                          dataKey="total_income" 
                          fill="#10b981"
                          name="Income"
                          radius={[4, 4, 0, 0]}
                        />
                        {/* Expenses bar - Red */}
                        <Bar 
                          dataKey="total_expenses" 
                          fill="#ef4444"
                          name="Expenses"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    );
                  })()}
                </ResponsiveContainer>
              </ErrorBoundary>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">
                <p>No daily spending data available for the selected period</p>
              </div>
            )}
          </div>
        </ErrorBoundary>

        {/* Spending by Category */}
        <ErrorBoundary>
          {loading && !spendingByCategoryError && spendingByCategory.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Spending by Category</h3>
              <div className="h-64 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                  <p>Loading spending data...</p>
                </div>
              </div>
            </div>
          ) : spendingByCategoryErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Spending by Category</h3>
              <p className="text-sm text-red-600">Failed to load spending by category</p>
              <p className="text-xs text-red-500 mt-1">{spendingByCategoryErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Spending by Category</h3>
              {/* Debug info - remove after testing */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mb-2 text-xs text-gray-400">
                  Categories loaded: {spendingByCategory.length} | 
                  Total: {formatCurrency(spendingByCategory[0]?.grand_total || 0)}
                </div>
              )}
              {spendingByCategory.length > 0 ? (() => {
                // Remove duplicates first by category_id (use first occurrence)
                const uniqueCategories = Array.from(
                  new Map(spendingByCategory.map(item => [item.category_id || item.category_name, item])).values()
                );
                
                // Calculate total for percentage calculation
                const total = uniqueCategories[0]?.grand_total || 0;
                
                // Filter categories to only show 9% or higher
                const filteredCategories = uniqueCategories.filter(cat => {
                  const percent = ((parseFloat(cat.total_amount || 0) / total) * 100);
                  return percent >= 9;
                });
                
                // Sort by amount descending
                const sortedCategories = [...filteredCategories].sort((a, b) => 
                  parseFloat(b.total_amount || 0) - parseFloat(a.total_amount || 0)
                );
                
                const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16', '#9ca3af'];
                
                // Dynamically adjust pie chart size based on number of categories
                const categoryCount = sortedCategories.length;
                const chartHeight = Math.max(280, Math.min(400, categoryCount * 40 + 200));
                const outerRadius = Math.min(90, chartHeight / 4);
                const innerRadius = outerRadius * 0.55;
                
                return (
                  <ErrorBoundary section="Category Chart" compact>
                    <ResponsiveContainer width="100%" height={chartHeight}>
                      <PieChart key={`pie-${appliedStartDate}-${appliedEndDate}-${selectedBusiness || 'all'}`}>
                        <Pie
                          data={sortedCategories}
                          dataKey="total_amount"
                          nameKey="category_name" onClick={(slice) => openSlice(slice?.payload || slice)} style={{ cursor: 'pointer' }}
                          cx="50%"
                          cy={categoryCount > 3 ? '45%' : '50%'}
                          outerRadius={outerRadius}
                          innerRadius={innerRadius}
                          paddingAngle={2}
                          label={(entry) => {
                            try {
                              const percent = ((parseFloat(entry.total_amount || 0) / total) * 100).toFixed(1);
                              return <text x={entry.x} y={entry.y} textAnchor={entry.textAnchor} dominantBaseline="central" fill="#374151" fontSize={12} style={{ cursor: 'pointer' }} onClick={() => openSlice(entry.payload || entry)}>{percent}%</text>;
                            } catch {
                              return '';
                            }
                          }}
                          labelLine={false}
                        >
                          {sortedCategories.map((entry, index) => (
                            <Cell 
                              key={`cell-${entry.category_id || index}`} onClick={() => openSlice(entry)} style={{ cursor: 'pointer' }}
                              fill={entry.color || colors[index % colors.length]} 
                              stroke="#fff"
                              strokeWidth={2}
                            />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name, props) => {
                            try {
                              const percent = ((parseFloat(value || 0) / total) * 100).toFixed(1);
                              return [
                                `${formatCurrency(parseFloat(value || 0))} (${percent}%)`,
                                props.payload?.category_name || name
                              ];
                            } catch {
                              return [formatCurrency(value || 0), name];
                            }
                          }}
                          contentStyle={{ 
                            backgroundColor: '#fff', 
                            border: '1px solid #d1d5db', 
                            borderRadius: '6px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          }}
                        />
                        <Legend 
                          verticalAlign="bottom" 
                          height={Math.min(sortedCategories.length * 18 + 10, 80)}
                          iconType="circle"
                          wrapperStyle={{ paddingTop: '10px' }}
                          formatter={(value) => {
                            try {
                              const data = sortedCategories.find(cat => cat.category_name === value);
                              if (data) {
                                const percent = ((parseFloat(data.total_amount || 0) / total) * 100).toFixed(1);
                                return `${value} (${percent}%)`;
                              }
                              return value;
                            } catch {
                              return value;
                            }
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </ErrorBoundary>
                );
              })() : (
                <div className="h-64 flex flex-col items-center justify-center text-gray-500">
                  <p className="text-lg font-medium mb-2">No Spending Data</p>
                  <p className="text-sm text-center max-w-sm">
                    No expense transactions found for the selected period. 
                    Add transactions with expense categories to see spending breakdown here.
                  </p>
                </div>
              )}
            </div>
          )}
        </ErrorBoundary>
      </div>

      {/* Monthly Trends */}
      <ErrorBoundary>
        {monthlyTrendsErrorFormatted ? (
          <div className="bg-red-50 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Monthly Trends (Last 12 Months)</h3>
            <p className="text-sm text-red-600">Failed to load monthly trends</p>
            <p className="text-xs text-red-500 mt-1">{monthlyTrendsErrorFormatted}</p>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Monthly Trends (Last 12 Months)</h3>
            {monthlyTrends.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={monthlyTrends.slice(0, 12).reverse()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short' })} />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <Bar dataKey="income" fill="#10b981" name="Income" />
                  <Bar dataKey="expenses" fill="#ef4444" name="Expenses" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-64 flex items-center justify-center text-gray-500">No data available</div>
            )}
          </div>
        )}
      </ErrorBoundary>

      {/* Recent Transactions & Subscriptions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Transactions */}
        <ErrorBoundary>
          {recentTransactionsErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">Recent Transactions</h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-red-600">Failed to load recent transactions</p>
                <p className="text-xs text-red-500 mt-1">{recentTransactionsErrorFormatted}</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold">Recent Transactions</h3>
              </div>
              <div className="divide-y">
                {recentTransactions.length > 0 ? (
                  recentTransactions.map((transaction) => (
                    <div key={transaction.id} className="p-4 flex justify-between items-center hover:bg-gray-50">
                      <div>
                        <p className="font-medium">{transaction.description}</p>
                        <p className="text-sm text-gray-600">{transaction.category_name} • {formatDate(transaction.transaction_date)}</p>
                      </div>
                      <p className={`font-semibold ${directionClass(transaction)}`}>
                        {signedAmountText(transaction, formatCurrency)}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500">No recent transactions</div>
                )}
              </div>
            </div>
          )}
        </ErrorBoundary>

        {/* Quick Stats */}
        <ErrorBoundary>
          {overviewErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
              <p className="text-sm text-red-600">Failed to load quick stats</p>
              <p className="text-xs text-red-500 mt-1">{overviewErrorFormatted}</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Quick Stats</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-blue-50 rounded">
                  <span className="text-gray-700">Active Subscriptions</span>
                  <span className="font-bold text-blue-600">{overview?.subscription_count || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-purple-50 rounded">
                  <span className="text-gray-700">Subscriptions per year (all active)</span>
                  <span className="font-bold text-purple-600">{formatCurrency(overview?.annual_subscription_cost || 0)}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded">
                  <span className="text-gray-700" title="Balance sheet: cash accounts + fixed assets + open invoices">Total assets (balance sheet)</span>
                  <span className="font-bold text-green-600">{formatCurrency(overview?.total_assets || 0)}</span>
                </div>
              </div>
            </div>
          )}
        </ErrorBoundary>
      </div>

      {/* v3.0.0 Features: Asset Expenses, Reconciliation, Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Asset Expense Summary */}
        <ErrorBoundary hidden={!can('/asset-expenses')}>
          {assetExpenseErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow">
              <div className="p-6 border-b flex items-center">
                <h3 className="text-lg font-semibold">Asset Expenses</h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-red-600">Failed to load asset expenses</p>
                <p className="text-xs text-red-500 mt-1">{assetExpenseErrorFormatted}</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b flex items-center">
                <h3 className="text-lg font-semibold">Asset Expenses</h3>
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {assetExpenseSummary.length > 0 ? (
                  assetExpenseSummary.slice(0, 5).map((item, idx) => {
                    const key = item.asset_id || item.id || `asset-expense-${idx}`;
                    return (
                      <div key={key} className="p-4 hover:bg-gray-50">
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-700 font-medium">Asset #{item.asset_id}</span>
                          <span className="font-bold text-gray-900">{formatCurrency(item.total_amount)}</span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">{item.expense_count} expenses</div>
                      </div>
                    );
                  })
                ) : (
                  <div className="p-8 text-center text-gray-500 text-sm">No asset expenses</div>
                )}
              </div>
            </div>
          )}
        </ErrorBoundary>

        {/* Reconciliation Status */}
        <ErrorBoundary hidden={!can('/reconciliation')}>
          {reconciliationsErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow">
              <div className="p-6 border-b flex items-center">
                <BanknotesIcon className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold">Bank Reconciliation</h3>
              </div>
              <div className="p-6">
                <p className="text-sm text-red-600">Failed to load reconciliations</p>
                <p className="text-xs text-red-500 mt-1">{reconciliationsErrorFormatted}</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b flex items-center">
                <BanknotesIcon className="h-5 w-5 text-blue-600 mr-2" />
                <h3 className="text-lg font-semibold">Bank Reconciliation</h3>
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {reconciliations.length > 0 ? (
                  reconciliations.map((recon) => (
                    <div key={recon.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-sm font-medium text-gray-900">Account #{recon.account_id}</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          recon.status === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {recon.status === 'completed' ? 'Completed' : 'In Progress'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600">
                        Statement: {formatCurrency(recon.statement_balance)}
                      </div>
                      <div className="text-xs text-gray-600">
                        Cleared: {formatCurrency(recon.cleared_amount || 0)}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500 text-sm">No active reconciliations</div>
                )}
              </div>
            </div>
          )}
        </ErrorBoundary>

        {/* Overdue Tasks */}
        <ErrorBoundary hidden={!can('/tasks')}>
          {overdueTasksErrorFormatted ? (
            <div className="bg-red-50 rounded-lg shadow">
              <div className="p-6 border-b flex items-center justify-between">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <h3 className="text-lg font-semibold">Overdue Tasks</h3>
                </div>
              </div>
              <div className="p-6">
                <p className="text-sm text-red-600">Failed to load overdue tasks</p>
                <p className="text-xs text-red-500 mt-1">{overdueTasksErrorFormatted}</p>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow">
              <div className="p-6 border-b flex items-center justify-between">
                <div className="flex items-center">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-600 mr-2" />
                  <h3 className="text-lg font-semibold">Overdue Tasks</h3>
                </div>
                {taskStats && (
                  <span className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-semibold">
                    {taskStats.overdue_tasks || 0}
                  </span>
                )}
              </div>
              <div className="divide-y max-h-64 overflow-y-auto">
                {overdueTasks.length > 0 ? (
                  overdueTasks.map((task) => (
                    <div key={task.id} className="p-4 hover:bg-gray-50 border-l-4 border-red-500">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-sm font-medium text-gray-900">{task.task_name}</span>
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          task.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                          task.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                          'bg-blue-100 text-blue-800'
                        }`}>
                          {task.priority}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        Due: {task.due_date ? formatDate(task.due_date) : 'No due date'}
                      </div>
                      <div className="flex items-center">
                        <div className="flex-1 bg-gray-200 rounded-full h-2 mr-2">
                          <div
                            className="bg-red-500 h-2 rounded-full"
                            style={{ width: `${task.completion_percentage}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">{task.completion_percentage}%</span>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="p-8 text-center text-gray-500 text-sm flex flex-col items-center">
                    <CheckCircleIcon className="h-12 w-12 text-green-500 mb-2" />
                    <span>No overdue tasks!</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </ErrorBoundary>
      </div>

      {/* Task Stats Summary */}
      <ErrorBoundary hidden={!can('/tasks')}>
        {taskStatsErrorFormatted ? (
          <div className="bg-red-50 rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Task Management Overview</h3>
            <p className="text-sm text-red-600">Failed to load task statistics</p>
            <p className="text-xs text-red-500 mt-1">{taskStatsErrorFormatted}</p>
          </div>
        ) : taskStats ? (
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">Task Management Overview</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded">
                <div className="text-3xl font-bold text-gray-900">{taskStats.total_tasks || 0}</div>
                <div className="text-sm text-gray-600 mt-1">Total Tasks</div>
              </div>
              <div className="text-center p-4 bg-green-50 rounded">
                <div className="text-3xl font-bold text-green-600">{taskStats.completed_tasks || 0}</div>
                <div className="text-sm text-gray-600 mt-1">Completed</div>
              </div>
              <div className="text-center p-4 bg-blue-50 rounded">
                <div className="text-3xl font-bold text-blue-600">{taskStats.in_progress_tasks || 0}</div>
                <div className="text-sm text-gray-600 mt-1">In Progress</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded">
                <div className="text-3xl font-bold text-red-600">{taskStats.overdue_tasks || 0}</div>
                <div className="text-sm text-gray-600 mt-1">Overdue</div>
              </div>
            </div>
          </div>
        ) : null}
      </ErrorBoundary>
      </div>
    </ErrorBoundary>
  );
};

export default Dashboard;
